import { NeededFiles, UploadItem } from "./interface";
import { Ce } from "@cimo/environment/dist/src/Main";

type HelperStoreBridge = {
    user: UserStore;
    setUser: (value: UserStore) => void;
    setIsSessionExpired: (value: boolean) => void;
};

let helperStore: HelperStoreBridge | null = null;

export const setHelperStore = (value: HelperStoreBridge): void => {
    helperStore = value;
};

export const ENV_NAME = Ce.checkVariable("ENV_NAME") || (process.env.ENV_NAME as string);

Ce.loadFile(`./env/${ENV_NAME}.env`);

export const DOMAIN = Ce.checkVariable("DOMAIN") || (process.env.DOMAIN as string);
export const TIME_ZONE = Ce.checkVariable("TIME_ZONE") || (process.env.TIME_ZONE as string);
export const LANG = Ce.checkVariable("LANG") || (process.env.LANG as string);
export const SERVER_PORT = Ce.checkVariable("SERVER_PORT") || (process.env.SERVER_PORT as string);
export const PATH_ROOT = Ce.checkVariable("PATH_ROOT");
export const NAME = Ce.checkVariable("REACT_APP_NAME") || (process.env.REACT_APP_NAME as string);
export const LABEL = Ce.checkVariable("REACT_APP_LABEL") || (process.env.REACT_APP_LABEL as string);
export const IS_DEBUG = Ce.checkVariable("REACT_APP_IS_DEBUG") || (process.env.REACT_APP_IS_DEBUG as string);
export const NODE_ENV = Ce.checkVariable("REACT_APP_NODE_ENV") || (process.env.REACT_APP_NODE_ENV as string);
export const URL_ROOT = Ce.checkVariable("REACT_APP_URL_ROOT") || (process.env.REACT_APP_URL_ROOT as string);
export const URL_BACKEND = Ce.checkVariable("REACT_APP_URL_BACKEND") || (process.env.REACT_APP_URL_BACKEND as string);
export const URL_AD_LOGIN = Ce.checkVariable("REACT_APP_URL_AD_LOGIN") || (process.env.REACT_APP_URL_AD_LOGIN as string);
export const URL_AD_REDIRECT = Ce.checkVariable("REACT_APP_URL_AD_REDIRECT") || (process.env.REACT_APP_URL_AD_REDIRECT as string);
export const PATH_CERTIFICATE_KEY = Ce.checkVariable("REACT_APP_PATH_CERTIFICATE_KEY");
export const PATH_CERTIFICATE_CRT = Ce.checkVariable("REACT_APP_PATH_CERTIFICATE_CRT");
export const PATH_CERTIFICATE_PEM = Ce.checkVariable("REACT_APP_PATH_CERTIFICATE_PEM");

Ce.loadFile(`./env/${ENV_NAME}.secret.env`);

export const REQUEST_TIMEOUT = 60000;
export const REQUEST_IS_ENCODED = false;

let checkSessionTimeoutId: NodeJS.Timeout | null = null;

export const writeLog = (tag: string, value: string | Record<string, unknown> | Error): void => {
    if (IS_DEBUG === "true") {
        // eslint-disable-next-line no-console
        console.log(`WriteLog => ${tag}: `, value);
    }
};

export const checkSessionTimeout = (userData: UserStore): void => {
    if (userData) {
        const tokenLifetimeMilisecond = 30 * 60 * 1000;

        if (checkSessionTimeoutId !== null) {
            clearTimeout(checkSessionTimeoutId);
            checkSessionTimeoutId = null;
        }

        checkSessionTimeoutId = setTimeout(() => {
            if (userData && userData.last_activity) {
                const lastActivity = new Date(userData.last_activity);
                const currentTime = new Date();
                const differenceTime = currentTime.getTime() - lastActivity.getTime();

                if (differenceTime > tokenLifetimeMilisecond) {
                    if (!helperStore) {
                        writeLog("error", "helperStore is not initialized");
                        return;
                    }

                    helperStore.setIsSessionExpired(true);
                    import("./actions/logout")
                        .then((module) => {
                            module.logout();
                        })
                        .catch((error: Error) => {
                            writeLog("error", error.message);
                        });
                } else {
                    checkSessionTimeout(userData);
                }
            }
        }, tokenLifetimeMilisecond);
    }
};

export const setLastActivity = () => {
    if (!helperStore) {
        writeLog("error", "helperStore is not initialized");
        return;
    }

    const currentTime = new Date();
    helperStore.setUser({
        ...helperStore.user,
        last_activity: currentTime.toISOString()
    });
};

export const localeFromEnvName = () => {
    let result = ENV_NAME.split("_").pop();

    if (result === "local") {
        result = "jp";
    }

    return result;
};

export function confirmPageChange(message: string | string[], callback: (ok: boolean) => void) {
    Promise.all([import("./actions/confirmBox"), import("./catchEndpoint")])
        .then(([confirmModule, catchEndpointModule]) => {
            confirmModule.confirmBox(message).catch(catchEndpointModule.catchEndpoint);
        })
        .catch((error: Error) => {
            writeLog("error", error.message);
        });

    callback(true);
}

export const removeStorage = (tag: string): void => {
    localStorage.removeItem(`${LABEL}_${tag}`);
};

export const routerHistoryPush = (nextUrl: string, parameterListValue?: Record<string, unknown>, title = "", soft = false): void => {
    let url = nextUrl;

    const [path, queryString] = url.split("?");
    const queryStringCleanedList: string[] = [];

    if (queryString) {
        const params = queryString.split("&");

        params.forEach((param) => {
            const [key, value] = param.split("=");

            const keyCleaned = encodeURIComponent(
                decodeURIComponent(key.replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;"))
            );

            if (value) {
                const valueCleaned = encodeURIComponent(
                    decodeURIComponent(value.replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;"))
                );

                queryStringCleanedList.push(`${keyCleaned}=${valueCleaned}`);
            } else {
                queryStringCleanedList.push(keyCleaned);
            }
        });
    }

    const pathCleaned = path.replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
    const urlCleaned = pathCleaned + (queryStringCleanedList.length > 0 ? "?" + queryStringCleanedList.join("&") : "");

    window.history.pushState(
        {
            prevUrl: window.location.pathname,
            parameterList: parameterListValue
        },
        title,
        urlCleaned
    );

    if (!soft) {
        window.location.href = urlCleaned;
    }
};

function smallNums(fullWidthNum: string): string {
    return fullWidthNum
        .replace(/[\uFF10-\uFF19]/g, (m) => {
            return String.fromCharCode(m.charCodeAt(0) - 0xfee0);
        })
        .replace(/−/g, "-");
}

function isJapanese(str: string): boolean {
    return !!String(str).match(/[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\uff00-\uff9f\u4e00-\u9faf\u3400-\u4dbf]/);
}

const setPageTitle = (pageName?: string, appName?: string): string => {
    const baseTitle = appName ? appName : "App";
    const pageTitle = pageName ? `${baseTitle} - ${pageName}` : baseTitle;
    document.title = pageTitle;
    return document.title;
};

type Modify<T, R> = Pick<T, Exclude<keyof T, keyof R>> & R;

type TableSortOrder = "asc" | "desc";

export enum ALERT_TYPE {
    ERROR = "error",
    SUCCESS = "success",
    ALERT = "alert"
}

export type Alert = {
    type: ALERT_TYPE;
    message: string;
    mode?: string;
};

export const ROUTE_TYPE = {
    HOME: "/",
    LOGIN: "/login",
    LOGOUT: "/logout",
    CLIENTS: "/clients",
    PROFILE: "/profile",
    LOGS: "/logs",
    WORKBOOKS: "/workbooks",
    WORKBOOK_ID: "/workbook/:workbookid",
    WORKBOOK_FILES: "/workbook/:workbookid/files",
    ADMIN: "/admin",
    ADMIN_PROFILE: "/admin/profile",
    ADMIN_CLIENTS: "/admin/clients",
    AD_LOGIN: "/ad/login",
    RESET_PASSWORD: "/resetpassword"
};

export type LogFilterType = {
    key: string;
    name: string;
    "data-at_id"?: string;
};

export type UserStore = {
    id?: number;
    username?: string;
    is_staff?: boolean;
    is_superuser?: boolean;
    is_support?: boolean;
    groupnames?: string[];
    last_activity?: string;
};

export enum UPLOAD_POPUP_TYPE {
    HIDDEN = 0,
    SHOW = 1,
    FINISHED = 2,
    ERROR = 3
}

export enum DOWNLOAD_STATUS {
    DOWNLOADED = 0,
    PROCESSING = 1,
    FINISH = 2,
    DOWNLOADABLE = 3
}

export const DOWNLOAD_STATUS_TEXT = {
    "0": "DOWNLOADED",
    "1": "PROCESSING",
    "2": "FINISH",
    "3": "DOWNLOADABLE"
};

type FileUploadStatus = {
    name: string;
    type: string;
    status: string;
};

class UploadValidator {
    protected filesStatus: FileUploadStatus[];
    protected nextValidator: UploadValidator | null = null;

    constructor(filesStatus: FileUploadStatus[]) {
        this.filesStatus = filesStatus;
    }

    setNext(validator: UploadValidator) {
        this.nextValidator = validator;
        return validator;
    }

    next(result: boolean) {
        if (this.nextValidator) {
            return this.nextValidator.validate();
        }
        return result;
    }

    validate() {
        let result = true;
        this.filesStatus.forEach((file) => {
            result = result && file.status === "DONE";
        });
        if (result) {
            this.next(result);
        }
        return result;
    }
}

export function uploadValidator(filename: string, files: NeededFiles[]) {
    const excelFiles: UploadItem[] = [];
    const csvFiles: UploadItem[] = [];
    files.forEach((file) => {
        const [name, type] = String(file.file_name).split(".");
        if (type == "xls" || type == "xlsx")
            excelFiles.push({
                name: name,
                type: type,
                status: file.file_status
            });
        else if (type == "csv")
            csvFiles.push({
                name: name,
                type: type,
                status: file.file_status
            });
    });

    const fileType = filename.split(".")[1];

    const excelHandler = new UploadValidator(excelFiles);
    const csvHandler = new UploadValidator(csvFiles);
    csvHandler.setNext(excelHandler);

    let result = true;
    if (fileType.toLowerCase() === "csv") {
        result = excelHandler.validate();
    } else if (fileType.toLowerCase() === "pdf") {
        result = csvHandler.validate();
    }
    return result;
}

export function pad(n: string, width = 2, z = "0") {
    return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
}

export { smallNums, isJapanese, setPageTitle, Modify, TableSortOrder };
